import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtEuro, MONTHS_EL } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { TrendingUp, BedDouble, Coins, CalendarDays, Activity, Wallet, AlertTriangle } from "lucide-react";
import MyDashboard from "@/pages/MyDashboard";
import { MenuTiles, FavoriteTiles } from "@/pages/MenuHub";
import StaffHome from "@/pages/StaffHome";
import { STAFF_ROLES } from "@/components/Layout";
import { KpiCard, Panel } from "@/components/dashboard/Kpi";
import { RevenueByDept } from "@/components/dashboard/RevenueByDept";
import { RevenueChart, OccupancyChart } from "@/components/dashboard/Charts";
import { TodayTiles, OpsTiles } from "@/components/dashboard/OpsTiles";
import { CostPanel } from "@/components/dashboard/CostPanel";
import { BudgetAlerts } from "@/components/dashboard/BudgetAlerts";
import { DataHealthCard } from "@/components/dashboard/DataHealthCard";
import { BudgetVarianceAlerts } from "@/components/finance/BudgetVarianceAlerts";

const pad = (n) => String(n).padStart(2, "0");
const pct = (v) => `${(v || 0).toFixed(1)}%`;

const usePl = (a, b, enabled) => useQuery({
  queryKey: ["dash-pl", a, b],
  queryFn: async () => (await api.get(`/finance/pl-period?date_from=${a}&date_to=${b}&operating=true`)).data,
  enabled,
});

export default function Dashboard() {
  const { user } = useAuth();
  const isStaff = !["owner", "hr", "manager"].includes(user?.role);
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  const { data: ov } = useQuery({ queryKey: ["dash-overview"], queryFn: async () => (await api.get("/pms/dashboard")).data, enabled: !isStaff });
  const d = now.getDate();
  const lyDay = Math.min(d, new Date(y - 1, m, 0).getDate());
  const { data: cur } = usePl(`${y}-${pad(m)}-01`, `${y}-${pad(m)}-${pad(d)}`, !isStaff);
  const { data: ly } = usePl(`${y - 1}-${pad(m)}-01`, `${y - 1}-${pad(m)}-${pad(lyDay)}`, !isStaff);
  const { data: stats } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard/stats")).data, enabled: !isStaff });
  const { data: alerts } = useQuery({ queryKey: ["budget-alerts", y], queryFn: async () => (await api.get(`/budget/alerts?year=${y}`)).data, enabled: !isStaff });

  if (STAFF_ROLES.includes(user?.role)) return <StaffHome />;
  if (isStaff) return <div className="space-y-6"><MenuTiles size="sm" /><MyDashboard /></div>;

  const rev = (d, k) => d?.[k === "ytd" ? "ytd" : "sections"] && (k === "ytd" ? d.ytd.sections.revenue.actual : d.sections.revenue.actual);
  const s = ov?.stats;

  return (
    <div className="space-y-6 md:space-y-8" data-testid="dashboard-page">
      <MenuTiles size="sm" />
      <FavoriteTiles />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Καλώς ήρθες, {user?.name}</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#45423C] mt-1">Επισκόπηση</h1>
        </div>
        <div className="text-sm text-[#6F6A60] flex items-center gap-2" data-testid="dash-period">
          <CalendarDays className="h-4 w-4" /> 1–{d} {MONTHS_EL[m]} {y} · από αρχή έτους · ίδιο διάστημα {y - 1}
        </div>
      </div>

      <BudgetAlerts alerts={alerts} year={y} />
      <DataHealthCard year={y} />
      <BudgetVarianceAlerts year={y} compact />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <KpiCard dark testid="kpi-rev-month" label={`Έσοδα 1–${d} ${MONTHS_EL[m]}`} value={rev(cur, "m") || 0} ly={rev(ly, "m") || 0} />
        <KpiCard dark testid="kpi-rev-ytd" label="Έσοδα από αρχή έτους" value={rev(cur, "ytd") || 0} ly={rev(ly, "ytd") || 0} />
        <KpiCard testid="kpi-occupancy" label="Πληρότητα μήνα" value={s?.mtd.occupancy} ly={s?.mtd_ly.occupancy} fmt={pct} sub={s && `YTD ${pct(s.ytd.occupancy)}`} />
        <KpiCard testid="kpi-adr" label="ADR μήνα" value={s?.mtd.adr} ly={s?.mtd_ly.adr} sub={s && `RevPAR ${fmtEuro(s.mtd.revpar)}`} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <Panel className="xl:col-span-3" testid="dash-revenue-dept" title="Έσοδα ανά τμήμα" icon={Coins}>
          <RevenueByDept cur={cur} ly={ly} />
        </Panel>
        <Panel className="xl:col-span-2" testid="dash-today" title="Σήμερα" icon={BedDouble}>
          <TodayTiles t={ov?.today_ops} />
          {s && (
            <div className="mt-4 grid grid-cols-3 gap-2 text-center" data-testid="dash-ytd-stats">
              {[["Πληρότητα YTD", pct(s.ytd.occupancy), pct(s.ytd_ly.occupancy)], ["ADR YTD", fmtEuro(s.ytd.adr), fmtEuro(s.ytd_ly.adr)], ["RevPAR YTD", fmtEuro(s.ytd.revpar), fmtEuro(s.ytd_ly.revpar)]].map(([l, v, p]) => (
                <div key={l} className="rounded-xl bg-[#F7F5F1] px-2 py-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-[#6F6A60]">{l}</div>
                  <div className="font-serif text-lg text-[#45423C] tabular">{v}</div>
                  <div className="text-[10px] text-[#B9AE9A] tabular">πέρσι {p}</div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel testid="dash-rev-chart" title={`Μηνιαία έσοδα ${y} vs ${y - 1}`} icon={TrendingUp}>
          <RevenueChart data={ov?.monthly_revenue} year={y} />
        </Panel>
        <Panel testid="dash-occ-chart" title="Πληρότητα ανά μήνα" icon={Activity}>
          {s ? <OccupancyChart data={s.monthly_occupancy} year={y} /> : <p className="text-sm text-[#6F6A60]">Δεν υπάρχουν στατιστικά Hotelizer.</p>}
        </Panel>
      </div>

      <Panel testid="dash-costs" title="Κόστος & αποτέλεσμα" icon={Wallet}>
        <CostPanel cur={cur} ly={ly} stats={stats} month={`1–${d} ${MONTHS_EL[m]}`} />
      </Panel>

      <Panel testid="dash-ops" title="Λειτουργία & εκκρεμότητες" icon={AlertTriangle}>
        <OpsTiles o={ov?.operations} />
      </Panel>
    </div>
  );
}
